import type { Application } from '#admin/features/exhibitor/api.ts'
import type { CompanyKind, ProfileBody } from '#admin/features/exhibitor/companies-api.ts'
import type { OpenApplicationInput, PassHolder } from '#admin/features/exhibitor/onboarding/api.ts'
import type { AdminOrderInput } from '#admin/features/exhibitor/service-orders-api.ts'

/**
 * The onboarding desk sends one request at a time, in the order the server's rules need:
 *
 * 1. The company, when it is new.
 * 2. The application, as a draft for the company.
 * 3. The stands. The submission then holds them as `reserved`.
 * 4. Submit, verify and approve. A service order needs an approved application.
 * 5. The service order: open, submit and invoice it. It comes before the settlement, because an
 *    invoiced stand (`payment_pending`) cannot take an order.
 * 6. The settlement: mark the stand paid, or issue its invoice. The server refuses to mark an
 *    invoiced application paid, so it is one or the other.
 * 7. The staff passes, and their badges. Only a paid stand has passes.
 *
 * Each request is its own transaction on the server. When one fails, the progress says what is
 * done, and the next run starts at the step that failed. Nothing is sent twice.
 */

export type DeskPlan = {
  /**
   * A company the edition knows, or a new one. `kinds` replaces the kinds of a known company: the
   * sponsor desk marks an exhibitor as a sponsor too.
   */
  company: { id: string; kinds?: readonly CompanyKind[] } | { body: ProfileBody }
  application: Omit<OpenApplicationInput, 'companyId'>
  boothIds: readonly string[]
  services: AdminOrderInput['items']
  settlement: { kind: 'paid'; note: string } | { kind: 'invoice' }
  staff: readonly PassHolder[]
  passTypeId: string | null
}

export type DeskProgress = {
  companyId: string | null
  application: Application | null
  boothsAssigned: boolean
  serviceOrderId: string | null
  serviceOrderStage: 'none' | 'created' | 'submitted' | 'invoiced'
  serviceInvoiceId: string | null
  /** How many staff passes are claimed, in the order of the plan. */
  claimed: number
  badgesIssued: boolean
  registrationId: string | null
}

export const NO_PROGRESS: DeskProgress = {
  companyId: null,
  application: null,
  boothsAssigned: false,
  serviceOrderId: null,
  serviceOrderStage: 'none',
  serviceInvoiceId: null,
  claimed: 0,
  badgesIssued: false,
  registrationId: null,
}

export type DeskStep =
  | 'company'
  | 'application'
  | 'stands'
  | 'approval'
  | 'services'
  | 'settlement'
  | 'badges'

/** The steps this plan takes, in order. */
export function stepsOf(plan: DeskPlan): DeskStep[] {
  return [
    ...('body' in plan.company || plan.company.kinds !== undefined ? (['company'] as const) : []),
    'application',
    'stands',
    'approval',
    ...(plan.services.length > 0 ? (['services'] as const) : []),
    'settlement',
    ...(plan.staff.length > 0 && plan.passTypeId !== null ? (['badges'] as const) : []),
  ]
}

const STEP_LABEL: Record<DeskStep, (plan: DeskPlan) => string> = {
  company: (plan) => ('body' in plan.company ? 'Add the company' : 'Mark the company as a sponsor'),
  application: () => 'Open the application',
  stands: () => 'Assign the stands',
  approval: () => 'Submit, verify and approve',
  services: () => 'Order and invoice the services',
  settlement: (plan) =>
    plan.settlement.kind === 'paid' ? 'Mark the stands paid' : 'Issue the stand invoice',
  badges: (plan) => `Issue ${plan.staff.length} ${plan.staff.length === 1 ? 'badge' : 'badges'}`,
}

export function stepLabel(step: DeskStep, plan: DeskPlan): string {
  return STEP_LABEL[step](plan)
}

/** The API calls the run makes. A parameter, so a test can give fakes. */
export type DeskApi = {
  createCompany: (body: ProfileBody) => Promise<{ id: string }>
  setCompanyKinds: (id: string, kinds: readonly CompanyKind[]) => Promise<unknown>
  openApplication: (input: OpenApplicationInput) => Promise<Application>
  assignBooths: (id: string, boothIds: string[]) => Promise<Application>
  submitApplication: (id: string) => Promise<Application>
  verifyApplication: (id: string) => Promise<Application>
  approveApplication: (id: string) => Promise<Application>
  createOrder: (applicationId: string, input: AdminOrderInput) => Promise<{ id: string }>
  submitServiceOrder: (id: string) => Promise<unknown>
  invoiceOrder: (id: string) => Promise<string | null>
  markApplicationPaid: (id: string, note?: string) => Promise<Application>
  createBoothInvoice: (id: string) => Promise<Application>
  claimPass: (applicationId: string, holder: PassHolder) => Promise<unknown>
  issuePasses: (
    applicationId: string,
    ticketTypeId: string,
  ) => Promise<{ registrationId: string | null }>
}

/** A run stopped at `step`. `progress` is what is done, for the next run. */
export class DeskStepError extends Error {
  readonly step: DeskStep
  readonly progress: DeskProgress

  constructor(step: DeskStep, progress: DeskProgress, cause: unknown) {
    super(cause instanceof Error ? cause.message : 'The request did not complete.', { cause })
    this.name = 'DeskStepError'
    this.step = step
    this.progress = progress
  }
}

/**
 * Run the plan from `start`. `onProgress` hears each step that is done and the step that starts,
 * so the summary can show where the run is.
 */
export async function runDesk(
  plan: DeskPlan,
  start: DeskProgress,
  deskApi: DeskApi,
  onProgress: (progress: DeskProgress, step: DeskStep) => void,
): Promise<DeskProgress> {
  let progress = start
  let step: DeskStep = 'company'
  const begin = (next: DeskStep) => {
    step = next
    onProgress(progress, step)
  }
  const save = (changes: Partial<DeskProgress>) => {
    progress = { ...progress, ...changes }
    onProgress(progress, step)
  }

  try {
    begin('company')
    let companyId = progress.companyId ?? ('id' in plan.company ? plan.company.id : null)
    if (companyId === null && 'body' in plan.company) {
      companyId = (await deskApi.createCompany(plan.company.body)).id
      save({ companyId })
    }
    if (progress.companyId === null && 'id' in plan.company && plan.company.kinds !== undefined) {
      await deskApi.setCompanyKinds(plan.company.id, plan.company.kinds)
      save({ companyId })
    }
    if (companyId === null) throw new Error('No company is selected.')

    begin('application')
    let application =
      progress.application ?? (await deskApi.openApplication({ ...plan.application, companyId }))
    save({ companyId, application })

    begin('stands')
    if (!progress.boothsAssigned) {
      application = await deskApi.assignBooths(application.id, [...plan.boothIds])
      save({ application, boothsAssigned: true })
    }

    begin('approval')
    if (application.status === 'draft') {
      application = await deskApi.submitApplication(application.id)
      save({ application })
    }
    if (application.status === 'submitted') {
      application = await deskApi.verifyApplication(application.id)
      save({ application })
    }
    if (application.status === 'pending_verification') {
      application = await deskApi.approveApplication(application.id)
      save({ application })
    }

    if (plan.services.length > 0) {
      begin('services')
      let orderId = progress.serviceOrderId
      if (orderId === null) {
        orderId = (await deskApi.createOrder(application.id, { items: [...plan.services] })).id
        save({ serviceOrderId: orderId, serviceOrderStage: 'created' })
      }
      if (progress.serviceOrderStage === 'created') {
        await deskApi.submitServiceOrder(orderId)
        save({ serviceOrderStage: 'submitted' })
      }
      if (progress.serviceOrderStage === 'submitted') {
        save({
          serviceInvoiceId: await deskApi.invoiceOrder(orderId),
          serviceOrderStage: 'invoiced',
        })
      }
    }

    begin('settlement')
    if (application.status === 'approved') {
      application =
        plan.settlement.kind === 'paid'
          ? await deskApi.markApplicationPaid(application.id, plan.settlement.note)
          : await deskApi.createBoothInvoice(application.id)
      save({ application })
    }

    if (plan.staff.length > 0 && plan.passTypeId !== null) {
      begin('badges')
      for (const holder of plan.staff.slice(progress.claimed)) {
        // oxlint-disable-next-line no-await-in-loop -- one pass at a time, so the count stays true
        await deskApi.claimPass(application.id, holder)
        save({ claimed: progress.claimed + 1 })
      }
      if (!progress.badgesIssued) {
        const issued = await deskApi.issuePasses(application.id, plan.passTypeId)
        save({ badgesIssued: true, registrationId: issued.registrationId })
      }
    }
  } catch (error) {
    throw new DeskStepError(step, progress, error)
  }
  return progress
}
